import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSupplierReturnById } from '@/services/supplierReturns';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Loader2, RotateCcw, Package, Laptop } from 'lucide-react';

interface SupplierReturnDetailProps {
  returnId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatMoney(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const SupplierReturnDetail: React.FC<SupplierReturnDetailProps> = ({ returnId, onClose }) => {
  const { data: returnRecord, isLoading, error } = useQuery({
    queryKey: ['supplier-returns', 'detail', returnId],
    queryFn: () => fetchSupplierReturnById(returnId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  if (error || !returnRecord) {
    return <ErrorAlert message={parseDatabaseError(error || 'Return record not found.')} />;
  }

  return (
    <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1 text-xs">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-brand-border">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">
            <RotateCcw className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-brand-black">Supplier Return #{returnRecord.return_number}</h3>
            <p className="text-brand-muted text-[11px]">
              Logged on {formatDate(returnRecord.return_date || returnRecord.created_at)}
            </p>
          </div>
        </div>
        <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase rounded-full">
          {returnRecord.status}
        </span>
      </div>

      {/* Meta Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-1">
          <span className="text-[10px] uppercase font-bold text-brand-muted block">Supplier</span>
          <p className="font-bold text-brand-black text-sm">{returnRecord.supplier?.name || 'N/A'}</p>
          {returnRecord.supplier?.phone && (
            <p className="text-brand-muted">Phone: {returnRecord.supplier.phone}</p>
          )}
        </div>

        <div className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-1">
          <span className="text-[10px] uppercase font-bold text-brand-muted block">Linked Purchase</span>
          {returnRecord.purchase ? (
            <p className="font-bold text-brand-black">
              #{returnRecord.purchase.purchase_number}{' '}
              {returnRecord.purchase.supplier_invoice_number ? `(Inv: ${returnRecord.purchase.supplier_invoice_number})` : ''}
            </p>
          ) : (
            <p className="text-brand-muted">Direct Supplier Return (No invoice link)</p>
          )}
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-3 gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div>
          <span className="text-[10px] uppercase font-bold text-emerald-800 block">Total Return Value</span>
          <span className="text-sm font-black text-emerald-900">{formatMoney(returnRecord.return_value)}</span>
        </div>
        <div>
          <span className="text-[10px] uppercase font-bold text-emerald-800 block">Payable Credit Applied</span>
          <span className="text-sm font-bold text-emerald-900">{formatMoney(returnRecord.credit_applied_to_payable)}</span>
        </div>
        <div>
          <span className="text-[10px] uppercase font-bold text-emerald-800 block">Cash Refund</span>
          <span className="text-sm font-bold text-emerald-900">{formatMoney(returnRecord.refund_amount)}</span>
        </div>
      </div>

      {/* Items Table */}
      <div className="space-y-2">
        <h4 className="font-bold text-brand-black uppercase text-[10px] tracking-wider">
          Returned Inventory Items ({returnRecord.items?.length || 0})
        </h4>
        <div className="border border-brand-border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-left">
            <thead className="bg-brand-bg border-b border-brand-border text-[10px] uppercase text-brand-muted font-bold">
              <tr>
                <th className="p-2.5">Item</th>
                <th className="p-2.5">Condition</th>
                <th className="p-2.5 text-center">Qty</th>
                <th className="p-2.5 text-right">Unit Cost</th>
                <th className="p-2.5 text-right">Line Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {returnRecord.items?.map((item) => (
                <tr key={item.id}>
                  <td className="p-2.5">
                    {item.item_type === 'laptop' ? (
                      <div className="flex items-center space-x-1.5 font-bold text-brand-black">
                        <Laptop className="w-3.5 h-3.5 text-brand-green" />
                        <span>
                          {item.laptop?.stock_id || 'Laptop'} — {item.laptop?.brand} {item.laptop?.model}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1.5 font-bold text-brand-black">
                        <Package className="w-3.5 h-3.5 text-brand-green" />
                        <span>{item.accessory?.name || 'Accessory'}</span>
                      </div>
                    )}
                  </td>
                  <td className="p-2.5 text-brand-muted">{item.condition_on_return || 'Defective'}</td>
                  <td className="p-2.5 text-center font-bold">{item.quantity}</td>
                  <td className="p-2.5 text-right">{formatMoney(item.unit_cost)}</td>
                  <td className="p-2.5 text-right font-bold">{formatMoney(item.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reason & Notes */}
      <div className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-1">
        <span className="text-[10px] uppercase font-bold text-brand-muted block">Reason</span>
        <p className="text-brand-black font-semibold">{returnRecord.reason}</p>
        {returnRecord.notes && (
          <p className="text-brand-muted mt-1 pt-1 border-t border-brand-border">{returnRecord.notes}</p>
        )}
      </div>

      <div className="flex justify-end pt-3 border-t border-brand-border">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};
